<template>
  <div class="auth-layout">
    <div class="auth-container" v-show="!isSettingUp">
      <div class="auth-header">
        <img
          src="/static/logo.png"
          alt="Pitaya"
          class="auth-logo"
          @error="handleLogoError"
        />
        <h1 class="auth-title">{{ $t("auth.welcome") }}</h1>
      </div>

      <div class="auth-content">
        <router-view />
      </div>

      <div class="auth-footer">
        <!-- <div class="language-selector">
          <select
            v-model="currentLocale"
            @change="changeLocale"
            class="locale-select"
          >
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </div> -->

        <p class="copyright">
          © {{ new Date().getFullYear() }} Pitaya. All rights reserved.
        </p>
      </div>
    </div>

    <!-- Setup overlay displayed during workspace initialization -->
    <transition name="fade">
      <div v-if="isSettingUp" class="setup-overlay" aria-live="polite">
        <div class="setup-card">
          <span class="setup-loader"></span>
          <h2 class="setup-title">We're setting things up…</h2>
          <p class="setup-subtitle">This will only take a few seconds.</p>
        </div>
      </div>
    </transition>

    <!-- Background decoration -->
    <div class="auth-background"></div>
  </div>
</template>

<script>
export default {
  name: "AuthLayout",
  data() {
    return {
      currentLocale: localStorage.getItem("locale") || "en",
      isSettingUp: false,
      setupStartTime: null,
      setupTimer: null,
    };
  },
  methods: {
    changeLocale() {
      localStorage.setItem("locale", this.currentLocale);

      // Get current route params
      const currentRoute = this.$route.path;
      const pathParts = currentRoute.split("/");

      // Replace locale in URL
      pathParts[1] = this.currentLocale;
      const newPath = pathParts.join("/");

      // Navigate to new locale path
      this.$router.push(newPath);
    },

    handleLogoError(event) {
      // Hide logo if image fails to load
      event.target.style.display = "none";
    },
  },

  created() {
    // Ensure current locale matches route
    const routeLocale = this.$route.params.locale;
    if (routeLocale && routeLocale !== this.currentLocale) {
      this.currentLocale = routeLocale;
      localStorage.setItem("locale", routeLocale);
    }
  },

  mounted() {
    // Listen for setup-started events from child views
    this.$root.$on("setup-started", (val) => {
      if (val) {
        // Setup started – show overlay immediately
        this.setupStartTime = Date.now();
        this.isSettingUp = true;

        // Clear any pending timer just in case
        if (this.setupTimer) {
          clearTimeout(this.setupTimer);
          this.setupTimer = null;
        }
      } else {
        // Setup finished – ensure overlay stays up for at least 5 s total
        const elapsed = Date.now() - (this.setupStartTime || Date.now());
        const remaining = 5000 - elapsed;

        if (remaining > 0) {
          this.setupTimer = setTimeout(() => {
            this.isSettingUp = false;
            this.setupTimer = null;
          }, remaining);
        } else {
          this.isSettingUp = false;
        }
      }
    });
  },

  beforeDestroy() {
    // Clean up any pending timer
    if (this.setupTimer) {
      clearTimeout(this.setupTimer);
    }
  },
};
</script>

<style scoped>
.auth-layout {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: url("@/assets/images/auth-background.jpg") center/cover no-repeat
    fixed;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  overflow: hidden;
}

.auth-container {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.15), 0 16px 32px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  padding: 3.5rem;
  width: 100%;
  max-width: 480px;
  min-height: 600px;
  position: relative;
  z-index: 10;
  margin: 2rem;
  transition: box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.auth-container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 50%,
    rgba(255, 255, 255, 0.1) 100%
  );
  border-radius: 24px;
  z-index: -1;
}

.auth-container:hover {
  box-shadow: 0 40px 80px rgba(0, 0, 0, 0.2), 0 20px 40px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.auth-header {
  text-align: center;
  margin-bottom: 2.5rem;
  position: relative;
}

.auth-header::after {
  content: "";
  position: absolute;
  bottom: -1rem;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    #667eea 50%,
    transparent 100%
  );
}

.auth-logo {
  height: 70px;
  width: auto;
  margin-bottom: 1.5rem;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
  transition: transform 0.3s ease;
}

.auth-logo:hover {
  transform: scale(1.05);
}

.auth-title {
  color: #2d3748;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.auth-content {
  margin-bottom: 2.5rem;
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.auth-footer {
  text-align: center;
  padding-top: 2rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  position: relative;
}

.auth-footer::before {
  content: "";
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(102, 126, 234, 0.3) 50%,
    transparent 100%
  );
}

.language-selector {
  margin-bottom: 1.5rem;
}

.locale-select {
  padding: 0.75rem 1.25rem;
  border: 2px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  color: #4a5568;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.locale-select:hover,
.locale-select:focus {
  border-color: #667eea;
  background: rgba(255, 255, 255, 0.95);
  outline: none;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.15);
  transform: translateY(-1px);
}

.copyright {
  color: #718096;
  font-size: 0.85rem;
  margin: 0;
  font-weight: 400;
  opacity: 0.8;
}

/* Background shapes - updated for more elegant effect */
.auth-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 1;
}

.bg-shape {
  position: absolute;
  border-radius: 50%;
  opacity: 0.03;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.8) 0%,
    rgba(255, 255, 255, 0.2) 70%,
    transparent 100%
  );
  backdrop-filter: blur(2px);
}

.shape-1 {
  width: 300px;
  height: 300px;
  top: 5%;
  left: 5%;
  animation: elegantFloat 12s ease-in-out infinite;
}

.shape-2 {
  width: 200px;
  height: 200px;
  top: 50%;
  right: 5%;
  animation: elegantFloat 16s ease-in-out infinite reverse;
}

.shape-3 {
  width: 150px;
  height: 150px;
  bottom: 15%;
  left: 50%;
  animation: elegantFloat 20s ease-in-out infinite;
}

@keyframes elegantFloat {
  0%,
  100% {
    transform: translateY(0px) translateX(0px) rotate(0deg);
  }
  25% {
    transform: translateY(-30px) translateX(10px) rotate(1deg);
  }
  50% {
    transform: translateY(-15px) translateX(-5px) rotate(-0.5deg);
  }
  75% {
    transform: translateY(-25px) translateX(15px) rotate(0.5deg);
  }
}

/* Responsive design */
@media (max-width: 768px) {
  .auth-container {
    margin: 1rem;
    padding: 2.5rem;
    max-width: none;
    border-radius: 20px;
  }

  .auth-title {
    font-size: 1.7rem;
  }

  .auth-logo {
    height: 60px;
  }

  .bg-shape {
    opacity: 0.02;
  }

  .shape-1 {
    width: 200px;
    height: 200px;
  }

  .shape-2 {
    width: 150px;
    height: 150px;
  }

  .shape-3 {
    width: 100px;
    height: 100px;
  }
}

@media (max-width: 480px) {
  .auth-container {
    padding: 2rem;
    border-radius: 18px;
    margin: 0.5rem;
  }

  .auth-title {
    font-size: 1.5rem;
  }

  .auth-logo {
    height: 50px;
  }

  .bg-shape {
    display: none;
  }
}

/* Fade transition for the overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.35s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Full-screen overlay */
.setup-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(
    circle at center,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(255, 255, 255, 0.85) 40%,
    rgba(255, 255, 255, 0.8) 70%
  );
  backdrop-filter: blur(6px);
}

/* Card that holds spinner + message */
.setup-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 2.5rem 3rem;
  max-width: 360px;
  width: 90%;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 10px 15px -3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

/* Circular loader */
.setup-loader {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #2c3e50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1.5rem;
  display: inline-block;
}

.setup-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.5rem;
}

.setup-subtitle {
  font-size: 0.95rem;
  color: #4a5568;
  margin: 0;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
